import React, { useState, useMemo } from 'react';
import { calculateScore } from '../utils';
import ShareResultsModal from './ShareResultsModal';

const ScoreInputModal = ({ players, currentHand, tricksPerHand, onSave, onCancel, editingHand = null, handHistory = [] }) => {
    // Initialize with edit data if available
    const [bids, setBids] = useState(() => {
        if (editingHand) {
            return players.map(player => editingHand.players[player.name]?.bid || '0');
        }
        return players.map(() => '0');
    });

    const [tricks, setTricks] = useState(() => {
        if (editingHand) {
            return players.map(player => editingHand.players[player.name]?.won || 0);
        }
        return players.map(() => 0);
    });

    const totalTricks = tricks.reduce((sum, t) => sum + Number(t), 0);
    const isEmptyHand = totalTricks === 0 && bids.every(b => b === '0');
    const isValid = totalTricks === tricksPerHand || isEmptyHand;

    const handleSave = () => {
        if (!isValid) return;

        const playerBids = {};
        const playerTricks = {};

        players.forEach((player, i) => {
            playerBids[player.name] = bids[i];
            playerTricks[player.name] = Number(tricks[i]);
        });

        onSave(playerBids, playerTricks);
    };

    const updateBid = (index, value) => {
        const numericValue = Number(value) || 0;
        const newBids = [...bids];
        newBids[index] = Math.max(0, Math.min(currentHand, numericValue)).toString();
        setBids(newBids);
    };

    const updateTrick = (index, value) => {
        const numericValue = Number(value) || 0;

        if (numericValue > tricks[index]) { // If trying to increment
            if (totalTricks >= tricksPerHand) {
                return; // Don't allow incrementing if total is already at max
            }
        }

        const newTricks = [...tricks];
        newTricks[index] = Math.max(0, Math.min(currentHand, numericValue));
        setTricks(newTricks);
    };

    // Get the bid value for this hand only
    const getCurrentBid = (playerIndex) => {
        return parseInt(bids[playerIndex] || 0);
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 font-['Inter']">
            <div className="w-full max-w-md rounded-3xl bg-[#1e293b] border border-white/10 shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
                <header className="p-4 sm:p-6 bg-[#0f172a]/50 border-b border-white/5">
                    <h2 className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-yellow-400 text-lg sm:text-xl font-bold text-center font-['Roboto_Slab']">
                        {editingHand ? `Edit Hand ${currentHand}` : `Enter Scores for Hand ${currentHand}`}
                    </h2>
                </header>

                {/* Total Bids Row */}
                <div className="p-2 bg-black/20 border-b border-white/5">
                    {/* Total Bids Summary - Centered at Top */}
                    <div className="flex justify-center mb-2">
                        <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-lg border border-amber-500/30 px-4 py-2 text-center min-w-[100px]">
                            <div key={bids.reduce((sum, bid) => sum + parseInt(bid || 0), 0)} className="text-2xl font-black text-amber-400 font-mono animate-pop">
                                {bids.reduce((sum, bid) => sum + parseInt(bid || 0), 0)}
                            </div>
                            <div className="text-xs font-bold uppercase tracking-wide text-amber-300 mt-0.5">
                                Total Bids
                            </div>
                        </div>
                    </div>

                    {/* Individual Player Bids */}
                    <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${players.length}, 1fr)` }}>
                        {players.map((player, i) => (
                            <div key={`total-${player.name}`} className="bg-black/40 rounded-lg border border-white/10 px-1 py-1.5 text-center">
                                <div className="text-base sm:text-lg font-black text-white font-mono">
                                    {getCurrentBid(i)}
                                </div>
                                <div className="text-[9px] font-bold uppercase tracking-tight text-gray-400 mt-0.5 truncate px-0.5">
                                    {player.name}
                                </div>
                                <div className="text-[8px] uppercase tracking-tight text-gray-500">
                                    Bid
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <main className="flex-1 p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto">
                    {players.map((player, i) => (
                        <div key={player.name} className="space-y-3">
                            <div className="flex items-center gap-2">
                                <p
                                    className="text-base sm:text-lg font-bold"
                                    style={{ color: player.color }}
                                >
                                    {player.name}
                                </p>
                                {i === 0 && (
                                    <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-[10px] font-bold uppercase tracking-wider border border-amber-500/30">
                                        First Bidder
                                    </span>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-3 sm:gap-4">
                                <div className="flex flex-col gap-2">
                                    <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Bid</span>
                                    <div className="flex items-center h-12 sm:h-14 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => updateBid(i, Number(bids[i]) - 1)}
                                            className="w-12 sm:w-14 h-full flex items-center justify-center rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-red-500/20 hover:border-red-500/30 active:scale-95 transition-all"
                                            aria-label="Decrease bid"
                                        >
                                            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg>
                                        </button>
                                        <div className="flex-1 h-full flex items-center justify-center rounded-xl bg-black/40 border border-white/10 shadow-inner">
                                            <span key={bids[i]} className="text-xl sm:text-2xl font-black text-white font-mono animate-pop">{bids[i]}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => updateBid(i, Number(bids[i]) + 1)}
                                            className="w-12 sm:w-14 h-full flex items-center justify-center rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-emerald-500/20 hover:border-emerald-500/30 active:scale-95 transition-all"
                                            aria-label="Increase bid"
                                        >
                                            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                        </button>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2">
                                    <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Won</span>
                                    <div className="flex items-center h-12 sm:h-14 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => updateTrick(i, tricks[i] - 1)}
                                            className="w-12 sm:w-14 h-full flex items-center justify-center rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-red-500/20 hover:border-red-500/30 active:scale-95 transition-all"
                                            aria-label="Decrease tricks"
                                        >
                                            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg>
                                        </button>
                                        <div className="flex-1 h-full flex items-center justify-center rounded-xl bg-black/40 border border-white/10 shadow-inner">
                                            <span key={tricks[i]} className="text-xl sm:text-2xl font-black text-white font-mono animate-pop">{tricks[i]}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => updateTrick(i, tricks[i] + 1)}
                                            className="w-12 sm:w-14 h-full flex items-center justify-center rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-emerald-500/20 hover:border-emerald-500/30 active:scale-95 transition-all"
                                            aria-label="Increase tricks"
                                        >
                                            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </main>

                <footer className="p-4 sm:p-6 bg-[#0f172a]/50 border-t border-white/5 space-y-4">
                    <div className="text-center">
                        <p
                            key={totalTricks}
                            className={`text-sm font-medium animate-pop ${!isValid ? 'text-red-400' : 'text-emerald-400'}`}
                        >
                            Total Won: {totalTricks} / {tricksPerHand}
                            {!isValid && !isEmptyHand && (
                                <span className="block text-xs mt-1 opacity-80">
                                    {totalTricks < tricksPerHand ? `Under by ${tricksPerHand - totalTricks}` : `Over by ${totalTricks - tricksPerHand}`}
                                </span>
                            )}
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="h-11 sm:h-12 rounded-xl border border-white/10 bg-white/5 text-gray-300 font-bold hover:bg-white/10 transition-colors text-sm sm:text-base"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={!isValid}
                            className="h-11 sm:h-12 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold shadow-lg shadow-orange-500/20 hover:shadow-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm sm:text-base"
                        >
                            {editingHand ? 'Update Hand' : 'Save Hand'}
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
};


const GameScreen = ({ players, setPlayers, numDecks, tricksPerHand, currentHand, setCurrentHand, handHistory, setHandHistory, gameState, setGameState, onNewGame, onShowStats, gameConfig, onHome, onLogout, onPauseGame, onQuitGame }) => {
    const [showModal, setShowModal] = useState(false);
    const [editingHand, setEditingHand] = useState(null);
    const [showQuitModal, setShowQuitModal] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);

    const getPlayersInHandOrder = (players, handNumber) => {
        const numPlayers = players.length;
        if (numPlayers === 0) return [];
        const startIndex = (handNumber - 1) % numPlayers;
        return [...players.slice(startIndex), ...players.slice(0, startIndex)];
    };



    const handleEditHand = (handNumber) => {
        const hand = handHistory.find(h => h.handNumber === handNumber);
        if (hand) {
            setEditingHand(hand);
            setShowModal(true);
        }
    };

    const handleSaveScores = (playerBids, playerTricks) => {
        if (editingHand) {
            // Editing existing hand
            const handIndex = handHistory.findIndex(h => h.handNumber === editingHand.handNumber);

            // Create updated hand
            const updatedHand = {
                handNumber: editingHand.handNumber,
                players: {}
            };

            // Calculate scores for this hand only (without cumulative totals yet)
            players.forEach(player => {
                let bid = playerBids[player.name];
                let won = playerTricks[player.name];

                // Get bags from the hand BEFORE this one
                const previousHand = handIndex > 0 ? handHistory[handIndex - 1] : null;
                const previousBags = previousHand ?
                    (previousHand.players[player.name]?.bags !== undefined ?
                        previousHand.players[player.name].bags : 0) : 0;

                const { score, bagPenalty, newBags } = calculateScore(bid, won, previousBags);

                updatedHand.players[player.name] = {
                    bid,
                    won,
                    score: score + bagPenalty,
                    bags: newBags
                };
            });

            // Update history with the edited hand
            const newHistory = [...handHistory];
            newHistory[handIndex] = updatedHand;

            // Recalculate all scores from this hand forward
            for (let i = handIndex; i < newHistory.length; i++) {
                const hand = newHistory[i];
                players.forEach(player => {
                    const previousHand = i > 0 ? newHistory[i - 1] : null;
                    const previousBags = previousHand ?
                        (previousHand.players[player.name]?.bags !== undefined ?
                            previousHand.players[player.name].bags : 0) : 0;

                    const { score, bagPenalty, newBags } = calculateScore(
                        hand.players[player.name].bid,
                        hand.players[player.name].won,
                        previousBags
                    );

                    hand.players[player.name].score = score + bagPenalty;
                    hand.players[player.name].bags = newBags;

                    // Calculate cumulative total
                    const previousTotal = previousHand ?
                        (previousHand.players[player.name]?.total || 0) : 0;
                    hand.players[player.name].total = previousTotal + hand.players[player.name].score;
                });
            }

            // Update player scores based on final cumulative totals
            const finalHand = newHistory[newHistory.length - 1];
            const updatedPlayers = players.map(player => ({
                ...player,
                score: finalHand.players[player.name]?.total || 0,
                bags: finalHand.players[player.name]?.bags || 0
            }));

            setHandHistory(newHistory);
            setPlayers(updatedPlayers);
            setEditingHand(null);
            setShowModal(false);
        } else {
            // Creating new hand
            const newHand = {
                handNumber: currentHand,
                players: {}
            };

            // Check if this is an empty hand (all 0 bids, 0 wins)
            // const isEmptyHand = Object.values(playerBids).every(b => b === '0') &&
            //    Object.values(playerTricks).every(t => t === 0);

            const updatedPlayers = players.map(player => {
                let bid = playerBids[player.name];
                let won = playerTricks[player.name];

                const { score, bagPenalty, newBags } = calculateScore(bid, won, player.bags);

                const handScore = score + bagPenalty;
                const totalScore = player.score + handScore;

                newHand.players[player.name] = {
                    bid,
                    won,
                    score: handScore,
                    total: totalScore,
                    bags: newBags
                };

                return {
                    ...player,
                    score: totalScore,
                    bags: newBags
                };
            });

            setPlayers(updatedPlayers);
            setHandHistory([...handHistory, newHand]);
            setShowModal(false);
            setCurrentHand(currentHand + 1);
        }
    };

    const winners = useMemo(() => {
        if (gameState !== 'finished') return [];
        if (players.length === 0) return [];
        const maxScore = Math.max(...players.map(p => p.score));
        return players.filter(p => p.score === maxScore);
    }, [gameState, players]);

    return (
        <div className="h-screen w-full bg-[#1a2332] font-['Inter'] overflow-hidden flex flex-col relative selection:bg-amber-500/30">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5 pointer-events-none">
                <div className="absolute top-0 left-0 w-full h-full"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5 C 35 15 45 15 45 25 C 45 35 30 50 30 50 C 30 50 15 35 15 25 C 15 15 25 15 30 5 Z' fill='%23ffffff' fill-rule='evenodd'/%3E%3C/svg%3E")`,
                        backgroundSize: '60px 60px'
                    }}
                />
            </div>

            {/* Ambient Glow */}
            <div className="absolute top-0 left-1/4 w-[50vw] h-[50vw] bg-purple-500/10 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-[50vw] h-[50vw] bg-blue-500/10 rounded-full blur-[100px] translate-x-1/2 translate-y-1/2 pointer-events-none" />

            {/* Header */}
            <header className="relative z-20 flex items-center justify-between px-4 py-3 bg-[#1a2332]/80 backdrop-blur-md border-b border-white/5 shadow-lg">
                <div className="flex items-center gap-2">
                    <button
                        onClick={onHome}
                        className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                        title="Home"
                    >
                        <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="currentColor" viewBox="0 0 20 20"><path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" /></svg>
                    </button>
                    {gameState === 'playing' && (
                        <button
                            onClick={onPauseGame}
                            className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-blue-500/30 transition-all duration-200"
                            title="Pause Game"
                        >
                            <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                        </button>
                    )}
                </div>

                <div className="flex flex-col items-center">
                    <h1 className="text-2xl sm:text-3xl font-black tracking-tight font-['Roboto_Slab'] text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-yellow-400 drop-shadow-sm uppercase">
                        {gameState === 'finished' ? 'Final Score' : 'Spades'}
                    </h1>
                    {gameState === 'playing' && (
                        <div className="flex items-center gap-2 mt-1">
                            <div className="h-[1px] w-8 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">Hand {currentHand}</span>
                            <div className="h-[1px] w-8 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {gameState === 'finished' && (
                        <button
                            onClick={() => setIsShareModalOpen(true)}
                            className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                            title="Share Results"
                        >
                            <svg className="w-5 h-5 text-amber-400 group-hover:text-amber-300 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                        </button>
                    )}

                    <button
                        onClick={onNewGame}
                        className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                        title="New Game"
                    >
                        <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                    </button>

                    {gameState !== 'finished' && (
                        <button
                            onClick={() => setShowQuitModal(true)}
                            className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-red-500/10 hover:border-red-500/30 transition-all duration-200"
                            title="Exit Game"
                        >
                            <svg className="w-5 h-5 text-gray-400 group-hover:text-red-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    )}

                    <button
                        onClick={onLogout}
                        className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                        title="Logout"
                    >
                        <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                    </button>
                </div>
            </header>

            {/* Main Content */}
            <main className="relative z-10 flex-1 overflow-hidden flex flex-col">
                {gameState === 'finished' && winners.length > 0 && (
                    <div className="relative px-4 py-12 text-center overflow-hidden">
                        {/* Background Glow */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-amber-500/20 rounded-full blur-[80px] animate-pulse" />

                        <div className="relative z-10">
                            <h2 className="text-sm font-bold uppercase tracking-[0.3em] text-amber-400 mb-4">
                                {winners.length > 1 ? 'Tie Game' : 'Winner'}
                            </h2>

                            <div className="flex flex-col items-center gap-2 mb-4">
                                {winners.map((winner, index) => (
                                    <div key={index} className="flex flex-col items-center">
                                        <p
                                            className="text-5xl sm:text-6xl font-black tracking-tighter drop-shadow-2xl animate-bounce"
                                            style={{ color: winner.color, textShadow: `0 0 30px ${winner.color}40` }}
                                        >
                                            {winner.name}
                                        </p>
                                        {index < winners.length - 1 && (
                                            <span className="text-2xl text-white/20 font-black my-1">&</span>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
                                <span className="text-gray-400 text-sm font-medium">Total Score</span>
                                <span className="text-2xl font-black text-white">{winners[0].score}</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex-1 overflow-auto">
                    <table className="w-full border-collapse text-left">
                        <thead className="sticky top-0 z-10 bg-[#1a2332] shadow-lg">
                            <tr>
                                <th className="sticky left-0 z-20 bg-[#1a2332] border-b border-r border-white/5 p-3 text-center min-w-[60px]">
                                    <span className="text-xs font-bold uppercase text-gray-500">#</span>
                                </th>
                                {players.map((player, i) => {
                                    const isLeader = players.length > 0 && player.score === Math.max(...players.map(p => p.score));
                                    return (
                                        <th key={i} className="border-b border-r border-white/5 bg-[#1a2332] p-3 min-w-[160px]">
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: player.color }} />
                                                    <span className="font-bold text-sm text-white">{player.name}</span>
                                                </div>
                                                {isLeader && (
                                                    <span className="text-amber-400 text-lg animate-bounce" title="Current Leader">👑</span>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-4 gap-1 text-[10px] font-bold uppercase text-gray-500">
                                                <div>Bid</div>
                                                <div>Won</div>
                                                <div>Pts</div>
                                                <div>Tot</div>
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from({ length: 13 }, (_, handNum) => {
                                const hand = handHistory.find(h => h.handNumber === handNum + 1);
                                const isCurrent = handNum + 1 === currentHand;
                                const handNumber = handNum + 1;

                                return (
                                    <tr key={`hand-${handNumber}`} className={`group ${isCurrent && gameState === 'playing' ? 'bg-amber-500/5' : 'hover:bg-white/[0.02]'}`}>
                                        <td className={`sticky left-0 z-10 border-b border-r border-white/5 p-3 text-center font-mono text-sm ${isCurrent && gameState === 'playing' ? 'bg-[#1a2332] text-amber-400 font-bold' : 'bg-[#1a2332] text-gray-500'}`}>
                                            <div className="flex items-center justify-center gap-2">
                                                <span>{handNumber}</span>
                                                {hand && (
                                                    <button
                                                        onClick={() => handleEditHand(handNumber)}
                                                        className="p-1 hover:bg-amber-500/20 rounded active:scale-95 transition-all"
                                                        aria-label={`Edit hand ${handNumber}`}
                                                        title={`Edit hand ${handNumber}`}
                                                    >
                                                        <svg className="w-3.5 h-3.5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        {players.map((player, i) => {
                                            const playerData = hand?.players[player.name];
                                            const displayBid = playerData?.bid;

                                            return (
                                                <td key={i} className="border-b border-r border-white/5 p-3">
                                                    <div className="grid grid-cols-4 text-sm">
                                                        <div className={`font-medium ${'text-gray-400'}`}>
                                                            {playerData && playerData.bid !== null ? displayBid : '-'}
                                                        </div>
                                                        <div className="text-gray-400">{playerData && playerData.won !== null ? playerData.won : '-'}</div>
                                                        <div className={`${playerData?.score < 0 ? 'text-red-400' : 'text-gray-300'}`}>
                                                            {playerData ? playerData.score : '-'}
                                                        </div>
                                                        <div className={`font-bold ${playerData?.total < 0 ? 'text-red-400' : 'text-white'}`}>
                                                            {playerData ? playerData.total : '-'}
                                                        </div>
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {gameState === 'finished' && (
                    <div className="p-6 flex justify-center bg-[#1a2332]/90 backdrop-blur-sm border-t border-white/5">
                        <button onClick={onShowStats} className="px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold rounded-full shadow-lg shadow-orange-500/20 hover:shadow-orange-500/30 hover:scale-105 transition-all">
                            View Statistics
                        </button>
                    </div>
                )}
            </main>

            {/* Footer - Player Pods */}
            <footer className="relative z-20 bg-[#131b26] border-t border-white/5 pb-safe pt-3 px-4">
                <div className="flex items-center gap-3 overflow-x-auto pb-4 no-scrollbar">
                    {players.map((player, i) => {
                        const isDanger = player.bags >= 8;
                        const sortedPlayers = [...players].sort((a, b) => b.score - a.score);
                        const rank = sortedPlayers.findIndex(p => p.name === player.name) + 1;

                        // Calculate last hand score change
                        const lastHand = handHistory[handHistory.length - 1];
                        const lastHandScore = lastHand?.players[player.name]?.score || 0;
                        const hasHistory = handHistory.length > 0;

                        return (
                            <div
                                key={i}
                                className={`flex-shrink-0 flex flex-col p-3 rounded-xl min-w-[120px] border transition-all relative overflow-hidden ${gameState === 'finished' && winners.some(w => w.name === player.name)
                                    ? 'bg-amber-500/10 border-amber-500/50'
                                    : 'bg-white/5 border-white/5'
                                    }`}
                            >
                                {/* Rank Badge */}
                                <div className="absolute top-0 right-0 p-1.5">
                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black border ${rank === 1 ? 'bg-amber-500 text-black border-amber-400' :
                                        rank === 2 ? 'bg-gray-400 text-black border-gray-300' :
                                            rank === 3 ? 'bg-orange-700 text-white border-orange-600' :
                                                'bg-gray-800 text-gray-400 border-gray-700'
                                        }`}>
                                        {rank}
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: player.color }} />
                                    <span className="text-xs font-bold truncate max-w-[80px]" style={{ color: player.color }}>
                                        {player.name}
                                    </span>
                                </div>

                                <div className="flex flex-col items-center mb-2">
                                    <span className={`text-3xl font-black tracking-tighter ${player.score < 0 ? 'text-red-400' : 'text-white'}`}>
                                        {player.score}
                                    </span>
                                    {hasHistory && (
                                        <span className={`text-[10px] font-bold ${lastHandScore >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {lastHandScore >= 0 ? '+' : ''}{lastHandScore}
                                        </span>
                                    )}
                                </div>

                                {/* Bags Progress Bar */}
                                <div className="w-full bg-black/40 rounded-full h-1.5 mb-1 overflow-hidden">
                                    <div
                                        className="h-full rounded-full bg-gray-500 transition-all duration-500"
                                        style={{ width: `${Math.min(player.bags * 10, 100)}%` }}
                                    />
                                </div>
                                <div className="flex justify-between items-center text-[9px] font-bold uppercase tracking-wider text-gray-500">
                                    <span>Bags</span>
                                    <span>{player.bags}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </footer>

            {/* FAB */}
            {gameState === 'playing' && (
                <div className="fixed bottom-32 sm:bottom-24 right-4 sm:right-6 z-50">
                    <button
                        onClick={() => setShowModal(true)}
                        className="flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-xl shadow-orange-500/30 hover:shadow-orange-500/40 hover:scale-105 transition-all group"
                    >
                        <span className="text-xl sm:text-2xl">✎</span>
                        <span className="font-bold text-sm sm:text-base">Record Hand {currentHand}</span>
                    </button>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <ScoreInputModal
                    players={getPlayersInHandOrder(players, editingHand ? editingHand.handNumber : currentHand)}
                    currentHand={editingHand ? editingHand.handNumber : currentHand}
                    tricksPerHand={editingHand ? editingHand.handNumber : currentHand}
                    onSave={handleSaveScores}
                    onCancel={() => {
                        setShowModal(false);
                        setEditingHand(null);
                    }}
                    editingHand={editingHand}
                    handHistory={handHistory}
                />
            )}

            <ShareResultsModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                players={players}
                handHistory={handHistory}
                gameConfig={gameConfig}
            />

            {/* Quit Confirmation Modal */}
            {showQuitModal && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="w-full max-w-md rounded-3xl bg-[#1e293b] border border-white/10 shadow-2xl p-8 transform transition-all scale-100">
                        <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-white text-center mb-2">Quit Game?</h2>
                        <p className="text-gray-400 text-center mb-8">
                            Are you sure you want to quit? This game <span className="text-red-400 font-bold">cannot be resumed</span> and all progress will be lost.
                        </p>
                        <div className="flex gap-4">
                            <button
                                onClick={() => setShowQuitModal(false)}
                                className="flex-1 px-6 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white font-bold hover:bg-white/10 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onQuitGame}
                                className="flex-1 px-6 py-3.5 rounded-xl bg-red-500 text-white font-bold hover:bg-red-600 shadow-lg shadow-red-500/25 transition-all"
                            >
                                Quit Game
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GameScreen;
